import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {LinkTrap} from '../../../../components/common/linkTrap/linkTrap';
import {useLinkNavigation} from '../../../../components/common/navigation/useLinkNavigation';
import {
  renderIntegrationTypeCategoryName,
  renderIntegrationTypeSupportedType,
} from '../../../../helpers/integrationTypesHelpers';
import {buildTeammatePath} from '../../../../helpers/modelHelpers';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {
  IntegrationTypeModel,
  IntegrationTypePublisherModel,
  IntegrationTypesSupportedTypesEnum,
} from '../../../../models/api/integrationTypesModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {TeammatesFoldersEnum} from '../../../../models/url/inboxesUrlModel';
import {SettingsAppsAppDetailsUrl} from '../../../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {updateSelectedSideFrameIdAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {pinFrameAction} from '../../../../store/data/preferences/preferencesActions';
import {SettingsAppsConfigureAppButton} from './configuration/settingsAppsConfigureAppButton';
import {SettingsAppsDetailsSidepanelSection} from './settingsAppsDetailsSidepanelSection';

/*
 * Props.
 */

interface SettingsAppsDetailsSidepanelProps {
  integrationType: IntegrationTypeModel;
  integration?: IntegrationModel;
  isIntegrationTypeEnabled: boolean;
  isIntegrationDisabledViaBilling: boolean;
  user: TeammateModel;
  url: SettingsAppsAppDetailsUrl;
  integrationFrameId?: string;
}

/*
 * Style.
 */

const StyledSidepanelWrapperDiv = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  flex-flow: column;
  border-radius: 16px;
  background: ${(p) => p.theme.greys.shade20};
  padding: 16px;
  font-weight: ${fontWeights.normal};
`;

const StyledInlineItemSpan = styled.span`
  &:not(:first-child)::before {
    content: ', ';
  }
`;

const StyledA = styled.a`
  display: block;
  color: ${(p) => p.theme.greys.shade80};
  text-decoration: underline;
`;

const StyledActionsWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;

  &:empty {
    display: none;
  }
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;

  &:last-child {
    margin-bottom: 24px;
  }
`;

const StyledButtonContentDiv = styled.div`
  width: 188px;
  text-align: center;
`;

const StyledLinkTrap = styled(LinkTrap)`
  display: flex;
  flex-flow: column;
  gap: 12px;
  line-height: 22px;
`;

/*
 * Component.
 */

export const SettingsAppsDetailsSidepanel: FC<SettingsAppsDetailsSidepanelProps> = (props) => {
  const {integrationType, user, integrationFrameId, integration} = props;
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();

  const onStartUsingAppClick = () => {
    navigateToLink(buildTeammatePath(user.id, TeammatesFoldersEnum.INBOX));
    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_START_USING, {
      integration_id: integration?.id,
      integration_type: integrationType.id,
    });
    if (integrationFrameId) {
      dispatch(updateSelectedSideFrameIdAction(integrationFrameId));
      dispatch(pinFrameAction(integrationFrameId));
    }
  };

  return (
    <StyledSidepanelWrapperDiv>
      {maybeRenderActions(props, onStartUsingAppClick)}
      {maybeRenderPublisherInformation(integrationType.publisher)}
      <SettingsAppsDetailsSidepanelSection
        title={<Trans id="settings-apps-details-sidepanel-app-type-title">App type</Trans>}
      >
        <div>
          {integrationType.supportedTypes.map((type) => (
            <StyledInlineItemSpan key={type}>{renderIntegrationTypeSupportedType(type)}</StyledInlineItemSpan>
          ))}
        </div>
      </SettingsAppsDetailsSidepanelSection>
      <SettingsAppsDetailsSidepanelSection
        title={<Trans id="settings-apps-details-sidepanel-categories-title">Categories</Trans>}
      >
        <div>
          {integrationType.categories.map((category) => (
            <StyledInlineItemSpan key={category}>
              {renderIntegrationTypeCategoryName(category)}
            </StyledInlineItemSpan>
          ))}
        </div>
      </SettingsAppsDetailsSidepanelSection>
      <SettingsAppsDetailsSidepanelSection
        title={<Trans id="settings-apps-details-sidepanel-available-on-title">Available on</Trans>}
      >
        <div>
          {integrationType.plans.map((plan) => (
            <StyledInlineItemSpan key={plan}>{plan}</StyledInlineItemSpan>
          ))}
        </div>
      </SettingsAppsDetailsSidepanelSection>
    </StyledSidepanelWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderPublisherInformation(publisher?: IntegrationTypePublisherModel) {
  if (!publisher) {
    return null;
  }
  return (
    <>
      <SettingsAppsDetailsSidepanelSection
        title={<Trans id="settings-apps-details-sidepanel-publisher-title">Publisher</Trans>}
      >
        {publisher.name}
      </SettingsAppsDetailsSidepanelSection>
      <SettingsAppsDetailsSidepanelSection
        title={
          <Trans id="settings-apps-details-sidepanel-publisher-resources-title">Publisher resources</Trans>
        }
      >
        <StyledLinkTrap>
          <StyledA href={publisher.website} target="_blank">
            <Trans id="settings-apps-details-sidepanel-publisher-resources-website">Website</Trans>
          </StyledA>
          <StyledA href={publisher.support} target="_blank">
            <Trans id="settings-apps-details-sidepanel-publisher-resources-support">Contact support</Trans>
          </StyledA>
          <StyledA href={publisher.privacyPolicy} target="_blank">
            <Trans id="settings-apps-details-sidepanel-publisher-resources-privacy-policy">
              Privacy policy
            </Trans>
          </StyledA>
        </StyledLinkTrap>
      </SettingsAppsDetailsSidepanelSection>
    </>
  );
}

function maybeRenderActions(props: SettingsAppsDetailsSidepanelProps, onStartUsingAppClick: Handler) {
  const {integrationType, isIntegrationTypeEnabled, url, isIntegrationDisabledViaBilling} = props;
  const isPlugin = integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.PLUGIN);

  return (
    <StyledActionsWrapperDiv>
      {isPlugin && isIntegrationTypeEnabled && (
        <StyledButtonWrapperDiv>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onStartUsingAppClick}>
            <StyledButtonContentDiv>
              <Trans id="settings-apps-sidepanel-action-start-using">Start using this app</Trans>
            </StyledButtonContentDiv>
          </Button>
        </StyledButtonWrapperDiv>
      )}
      <SettingsAppsConfigureAppButton
        integrationType={integrationType}
        url={url}
        isIntegrationTypeEnabled={isIntegrationTypeEnabled}
        isIntegrationDisabledViaBilling={isIntegrationDisabledViaBilling}
      />
    </StyledActionsWrapperDiv>
  );
}
